<template>
    <asli-form @submitted="goNext">
        <asli-form-section>
            <asli-radio
                v-if="showLocksQuestion"
                id="has_locks_on_all_doors_windows"
                name="has_locks_on_all_doors_windows"
                label="Are all external doors fitted with secure key operated, manufacture approved locks and windows fitted with appropriate latches?"
                rules="required"
                v-model="hasLocks"
                :options="lists?.yes_no"
                :is-yes-no-option="true"
                return-boolean
                @change="commitRisk"
            />
        </asli-form-section>
        <asli-form-section v-if="hasGeneralAssumptions" title="General" heading-level="h2">
            <div v-for="(question, index) in generalAssumptions" :key="buildId('general', index)">
                <asli-radio
                    :id="buildId('general', index)"
                    :name="buildId('general', index)"
                    :label="question.question_text"
                    rules="required"
                    v-model="question.value"
                    :options="lists?.yes_no"
                    :is-yes-no-option="true"
                    return-boolean
                    @change="commitRisk"
                />
            </div>
        </asli-form-section>
        <asli-form-section title="Property" heading-level="h2">
            <div v-for="(question, index) in propertyAssumptions" :key="buildId('property', index)">
                <asli-radio
                    :id="buildId('property', index)"
                    :name="buildId('property', index)"
                    :label="question.question_text"
                    rules="required"
                    v-model="question.value"
                    :options="lists?.yes_no"
                    :is-yes-no-option="true"
                    return-boolean
                    @change="commitRisk"
                />
            </div>
        </asli-form-section>
        <asli-form-section title="Insured" heading-level="h2">
            <div v-for="(question, index) in insuredAssumptions" :key="buildId('insured', index)">
                <asli-radio
                    :id="buildId('insured', index)"
                    :name="buildId('insured', index)"
                    :label="question.question_text"
                    rules="required"
                    v-model="question.value"
                    :options="lists?.yes_no"
                    :is-yes-no-option="true"
                    return-boolean
                    @change="commitRisk"
                />
            </div>
        </asli-form-section>
        <document-confirmation v-if="showDocumentConfirmation" ref="documentConfirmation" />
        <form-buttons @back="goBack" />
    </asli-form>
</template>

<script lang="ts" setup>
import { AsliForm, AsliFormSection, AsliRadio } from "@mbs-npm/anpost-ui";
import { computed, nextTick, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { useMainStore } from "@/stores/main";
import { useProposalStore } from "@/stores/risk/risk";
import { useListStore } from "@/stores/lists/lists";
import { AssumptionDetails } from "@/stores/risk/types/types";
import { INSURER_NAMES } from "@/static/constants";
import { ROUTE_NAMES } from "@/router/types";
import FormButtons from "@/components/FormButtons.vue";
import { isInsurer } from "@/utils";
import DocumentConfirmation from "./MarketingPage/DocumentConfirmation.vue";

const mainStore = useMainStore();
const proposalStore = useProposalStore();
const router = useRouter();
const listStore = useListStore();

const lists = computed(() => listStore?.lists);
const storedRisk = computed(() => proposalStore.proposal.risk);
const displayCachedQuotes = computed(() => proposalStore.displayCachedQuotes);

const selectedQuote = computed(() => proposalStore.proposal.quote);
const cachedQuoteId = ref<number | null>(null);
const cachedQuote = computed(() => proposalStore.cachedQuotes.find(quote => quote.id === (cachedQuoteId.value ?? 0)));

const assumptionsToDisplay = computed(() =>
    displayCachedQuotes.value ? cachedQuote.value?.assumptions : selectedQuote.value?.assumptions
);

const generalAssumptions = computed(() => assumptionsToDisplay.value?.["general"] || []);
const propertyAssumptions = computed(() => assumptionsToDisplay.value?.["property"] || []);
const insuredAssumptions = computed(() => assumptionsToDisplay.value?.["insured"] || []);
const hasGeneralAssumptions = computed(() => generalAssumptions.value.length > 0);
const isOfflineQuote = computed(() => proposalStore.proposal.recalled_from_offline);
const isAllianzQuote = computed(() => isInsurer(selectedQuote.value, INSURER_NAMES.Allianz));
const showLocksQuestion = computed(() => (isAllianzQuote.value && !isOfflineQuote.value) || displayCachedQuotes.value);
const needsRequote = computed(
    () =>
        isAllianzQuote.value &&
        !isOfflineQuote.value &&
        !showDocumentConfirmation.value &&
        (!hasLocks.value || locksValueChanged.value)
);

function getLocksDefaultAnswer() {
    const riskValue = storedRisk.value.property_details.security_details.has_locks_on_all_doors_windows;
    return riskValue ? null : riskValue;
}

const hasLocks = ref(showLocksQuestion.value ? getLocksDefaultAnswer() : true);
const locksValueChanged = ref(false);

const showDocumentConfirmation = ref(false);
const documentConfirmation = ref<InstanceType<typeof DocumentConfirmation> | null>(null);

const buildId = (category: string, index: number) => `assumptions_${category}_${index}`;

const mergedObjects = computed(() => [
    ...propertyAssumptions.value,
    ...insuredAssumptions.value,
    ...generalAssumptions.value
]);

const correctAssumptions = computed(() =>
    mergedObjects.value.every((assumption: AssumptionDetails) => assumption.expected_answer === assumption.value)
);

function commitRisk() {
    proposalStore.patchPropertyDetails({
        ...storedRisk.value.property_details,
        security_details: {
            ...storedRisk.value.property_details.security_details,
            has_locks_on_all_doors_windows: hasLocks.value
        }
    });
}

watch(
    () => hasLocks.value,
    (newValue, oldValue) => {
        if (newValue === true && oldValue === false) {
            locksValueChanged.value = true;
        }
        showDocumentConfirmation.value = false;
    }
);

watch(
    correctAssumptions,
    () => {
        proposalStore.patchAssumptionsAccepted(correctAssumptions.value);
    },
    { deep: true }
);

watch(
    () => displayCachedQuotes.value,
    newVal => {
        if (newVal) {
            applySavedAssumptionValues();
        }
    }
);

function showCheckboxes() {
    showDocumentConfirmation.value = true;
    setTimeout(() => {
        if (documentConfirmation.value) {
            documentConfirmation.value.scrollToCheckboxes(), 100;
        }
    });
}

function goBack() {
    const routeName = isOfflineQuote.value ? ROUTE_NAMES.YourQuotes : ROUTE_NAMES.QuoteConfirm;
    router.push({
        name: routeName,
        query: {
            uuid: proposalStore.proposal.uuid
        }
    });
}

function applySavedAssumptionValues() {
    const applyToQuestions = questions => {
        if (!questions) return;
        questions.forEach(question => {
            question.value = question.expected_answer;
        });
    };
    applyToQuestions(generalAssumptions.value);
    applyToQuestions(propertyAssumptions.value);
    applyToQuestions(insuredAssumptions.value);
}

async function handleRequote() {
    mainStore.setLoadingStatePopupText("Checking quote details...");
    mainStore.setLoadingState(true);

    try {
        cachedQuoteId.value = proposalStore.proposal.quote?.id;
        await proposalStore.recalculateQuote(false, null);

        locksValueChanged.value = false;

        const validQuotes = proposalStore.validQuotes;
        if (validQuotes.length === 0) {
            await router.push({ name: ROUTE_NAMES.Declined });
            return false;
        }

        await nextTick();
        applySavedAssumptionValues();

        return true;
    } catch (error) {
        return false;
    } finally {
        mainStore.setLoadingState(false);
    }
}

async function goNext() {
    if (displayCachedQuotes.value) {
        return;
    }

    mainStore.setLoadingState(true);
    try {
        commitRisk();

        if (correctAssumptions.value) {
            if (needsRequote.value) {
                const requoteSuccessful = await handleRequote();
                if (!requoteSuccessful) {
                    return;
                }
            }

            if (showDocumentConfirmation.value) {
                if (
                    hasLocks.value !== storedRisk.value.property_details.security_details.has_locks_on_all_doors_windows
                ) {
                    await proposalStore.confirmRisk({
                        uuid: proposalStore.proposal.uuid as string,
                        risk: storedRisk.value,
                        isMonthly: proposalStore.proposal.is_pay_monthly
                    });
                    await handleRequote();
                }

                const paymentRouteName = proposalStore.proposal.is_pay_monthly
                    ? ROUTE_NAMES.DirectDebit
                    : ROUTE_NAMES.Payment;

                router.push({
                    name: paymentRouteName,
                    query: {
                        uuid: proposalStore.proposal.uuid
                    }
                });
            } else {
                showCheckboxes();
            }
        } else {
            router.push({ name: ROUTE_NAMES.Declined });
        }
    } finally {
        mainStore.setLoadingState(false);
    }
}
</script>
